import React from 'react';
import DocsComponent from '../../components/Docs/DocsComponent';
import { Language } from '../../types';

interface DocsTemplateProps {
  language: Language;
  onBack?: () => void;
}

const DocsTemplate: React.FC<DocsTemplateProps> = ({ language, onBack }) => {
  
  const DOCS_CONTENT = [
    {
      id: 'intro',
      title: { ar: 'مقدمة: منصة لهجة السيادية', en: 'Introduction: Lahja Sovereign AI' },
      icon: 'fa-star',
      content: {
        ar: (
          <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <section>
               <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-4 italic border-l-4 border-emerald-500 pl-4">الرؤية والمهمة الوطنية</h3>
               <p className="text-base leading-loose text-slate-600 dark:text-slate-300">
                  "لهجة" هي أول منصة ذكاء اصطناعي سعودية سيادية متكاملة، صُممت لتكون الجسر التقني بين قوة الحوسبة السحابية وعمق الثقافة المحلية. تهدف المنصة إلى تمكين الأفراد والشركات من بناء حلول ذكية تتحدث وتفهم بلسان سعودي أصيل، مع ضمان كامل لسيادة البيانات وخصوصيتها داخل البنية التحتية المحلية.
               </p>
            </section>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               <div className="p-8 bg-emerald-50 dark:bg-emerald-500/5 rounded-[40px] border border-emerald-100 dark:border-emerald-500/10 transition-all hover:scale-105">
                  <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-emerald-500/20"><i className="fas fa-microchip"></i></div>
                  <h4 className="font-black text-emerald-600 mb-3">الدقة اللغوية</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed uppercase tracking-tighter font-bold">معالجة متقدمة لـ 4 لهجات رئيسية: نجدي، حجازي، جنوبي، وشرقي.</p>
               </div>
               <div className="p-8 bg-blue-50 dark:bg-blue-500/5 rounded-[40px] border border-blue-100 dark:border-blue-500/10 transition-all hover:scale-105">
                  <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-blue-500/20"><i className="fas fa-shield-halved"></i></div>
                  <h4 className="font-black text-blue-600 mb-3">السيادة الرقمية</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed uppercase tracking-tighter font-bold">تشفير بمعايير عسكرية لضمان أمن المعلومات الوطنية والخاصة.</p>
               </div>
               <div className="p-8 bg-purple-50 dark:bg-purple-500/5 rounded-[40px] border border-purple-100 dark:border-purple-500/10 transition-all hover:scale-105">
                  <div className="w-12 h-12 bg-purple-500 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-purple-500/20"><i className="fas fa-network-wired"></i></div>
                  <h4 className="font-black text-purple-600 mb-3">تكامل الأنظمة</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed uppercase tracking-tighter font-bold">ربط مباشر مع الواتساب، خرائط جوجل، وأنظمة الشركات (ERP).</p>
               </div>
            </div>
          </div>
        ),
        en: (
          <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <section>
               <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-4 italic border-l-4 border-emerald-500 pl-4">Vision & National Mission</h3>
               <p className="text-base leading-loose text-slate-600 dark:text-slate-300">
                  "Lahja" is the first integrated Saudi sovereign AI platform, designed to be the technical bridge between cloud computing power and local cultural depth. The platform aims to empower individuals and businesses to build smart solutions that speak and understand with an authentic Saudi tongue, while ensuring complete data sovereignty and privacy within local infrastructure.
               </p>
            </section>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               <div className="p-8 bg-emerald-50 dark:bg-emerald-500/5 rounded-[40px] border border-emerald-100 dark:border-emerald-500/10 transition-all hover:scale-105">
                  <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-emerald-500/20"><i className="fas fa-microchip"></i></div>
                  <h4 className="font-black text-emerald-600 mb-3">Linguistic Precision</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed uppercase tracking-tighter font-bold">Advanced processing for 4 main dialects: Najdi, Hijazi, Southern, and Eastern.</p>
               </div>
               <div className="p-8 bg-blue-50 dark:bg-blue-500/5 rounded-[40px] border border-blue-100 dark:border-blue-500/10 transition-all hover:scale-105">
                  <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-blue-500/20"><i className="fas fa-shield-halved"></i></div>
                  <h4 className="font-black text-blue-600 mb-3">Digital Sovereignty</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed uppercase tracking-tighter font-bold">Military-grade encryption to ensure the security of national and private information.</p>
               </div>
               <div className="p-8 bg-purple-50 dark:bg-purple-500/5 rounded-[40px] border border-purple-100 dark:border-purple-500/10 transition-all hover:scale-105">
                  <div className="w-12 h-12 bg-purple-500 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-purple-500/20"><i className="fas fa-network-wired"></i></div>
                  <h4 className="font-black text-purple-600 mb-3">Systems Integration</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed uppercase tracking-tighter font-bold">Direct linking with WhatsApp, Google Maps, and Enterprise Resource Planning (ERP).</p>
               </div>
            </div>
          </div>
        )
      }
    },
    {
      id: 'conversational-engine',
      title: { ar: 'محرك المحادثة واللهجات', en: 'Conversational Engine & Dialects' },
      icon: 'fa-comments',
      content: {
        ar: (
          <div className="space-y-8">
            <section>
               <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">التواصل الوجداني واللغوي</h3>
               <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  يعتمد محركنا على تقنية "المعالجة السياقية العميقة" التي لا تكتفي بترجمة الكلمات، بل تفهم الهرجة، المثل الشعبي، وحتى النبرة العاطفية للمستخدم.
               </p>
            </section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {[
                 { title: 'اللهجة النجدية', desc: 'تتميز بالرسمية والقوة، مثالية للأعمال والخدمات الحكومية.' },
                 { title: 'اللهجة الحجازية', desc: 'تتميز باللطف والدفء، مناسبة لقطاع الضيافة والتجارة.' },
                 { title: 'اللهجة الجنوبية', desc: 'تتميز بالترحيب العالي والمفردات التراثية العميقة.' },
                 { title: 'اللهجة الشرقية', desc: 'تتميز بالهدوء والأسلوب الحديث الذي يجمع بين الأصالة والتطور.' }
               ].map((d, i) => (
                  <div key={i} className="flex items-center gap-4 p-5 bg-white dark:bg-white/[0.03] rounded-[32px] border border-slate-100 dark:border-white/5 shadow-sm">
                     <span className="w-1.5 h-10 bg-emerald-500 rounded-full"></span>
                     <div>
                        <h4 className="text-sm font-black dark:text-white">{d.title}</h4>
                        <p className="text-[11px] text-slate-500 font-medium">{d.desc}</p>
                     </div>
                  </div>
               ))}
            </div>
          </div>
        ),
        en: (
          <div className="space-y-8">
            <section>
               <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">Emotional & Linguistic Connection</h3>
               <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Our engine relies on "Deep Contextual Processing" technology, which doesn't just translate words, but understands slang, folk proverbs, and even the user's emotional tone.
               </p>
            </section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {[
                 { title: 'Najdi Dialect', desc: 'Characterized by formality and strength, ideal for business and government services.' },
                 { title: 'Hijazi Dialect', desc: 'Characterized by kindness and warmth, suitable for hospitality and trade.' },
                 { title: 'Southern Dialect', desc: 'Characterized by high hospitality and deep traditional vocabulary.' },
                 { title: 'Eastern Dialect', desc: 'Characterized by calmness and a modern style that blends heritage with progress.' }
               ].map((d, i) => (
                  <div key={i} className="flex items-center gap-4 p-5 bg-white dark:bg-white/[0.03] rounded-[32px] border border-slate-100 dark:border-white/5 shadow-sm">
                     <span className="w-1.5 h-10 bg-emerald-500 rounded-full"></span>
                     <div>
                        <h4 className="text-sm font-black dark:text-white">{d.title}</h4>
                        <p className="text-[11px] text-slate-500 font-medium">{d.desc}</p>
                     </div>
                  </div>
               ))}
            </div>
          </div>
        )
      }
    },
    {
      id: 'flow-canvas',
      title: { ar: 'معمارية مركز الأتمتة (Flows)', en: 'Flow Canvas Architecture' },
      icon: 'fa-diagram-project',
      content: {
        ar: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">البرمجة المرئية بدون كود</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                   مركز التدفق (Flow Canvas) هو المختبر الذي تبني فيه "منطق" أعمالك. بدلاً من كتابة آلاف السطور البرمجية، يمكنك سحب العقد وربطها.
                </p>
             </section>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-slate-900 rounded-[32px] border border-slate-700 shadow-xl group hover:border-amber-500/50 transition-all">
                   <h4 className="text-amber-500 font-black text-xs mb-3 uppercase tracking-widest flex items-center gap-2"><i className="fas fa-bolt"></i> المشغلات (Triggers)</h4>
                   <p className="text-[11px] text-slate-400 font-medium">هي البداية؛ مثل وصول رسالة واتساب، حدوث ساعة معينة، أو استقبال طلب HTTP من موقعك.</p>
                </div>
                <div className="p-6 bg-slate-900 rounded-[32px] border border-slate-700 shadow-xl group hover:border-purple-500/50 transition-all">
                   <h4 className="text-purple-500 font-black text-xs mb-3 uppercase tracking-widest flex items-center gap-2"><i className="fas fa-brain"></i> المعالجات (Processors)</h4>
                   <p className="text-[11px] text-slate-400 font-medium">هنا يحدث الذكاء؛ مثل تحليل المشاعر، تلخيص النص، أو تحويل الصوت إلى نص (STT).</p>
                </div>
                <div className="p-6 bg-slate-900 rounded-[32px] border border-slate-700 shadow-xl group hover:border-blue-500/50 transition-all">
                   <h4 className="text-blue-500 font-black text-xs mb-3 uppercase tracking-widest flex items-center gap-2"><i className="fas fa-paper-plane"></i> الإجراءات (Actions)</h4>
                   <p className="text-[11px] text-slate-400 font-medium">هي الخاتمة؛ مثل إرسال إيميل، تحديث قاعدة البيانات، أو الرد الصوتي المباشر.</p>
                </div>
                <div className="p-6 bg-slate-900 rounded-[32px] border border-slate-700 shadow-xl group hover:border-emerald-500/50 transition-all">
                   <h4 className="text-emerald-500 font-black text-xs mb-3 uppercase tracking-widest flex items-center gap-2"><i className="fas fa-link"></i> الربط المنطقي (Edges)</h4>
                   <p className="text-[11px] text-slate-400 font-medium">الخطوط التي تنقل البيانات من عقدة إلى أخرى مع إمكانية وضع شروط منطقية (If/Else).</p>
                </div>
             </div>
          </div>
        ),
        en: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">No-Code Visual Programming</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                   The Flow Canvas is the laboratory where you build your business "logic." Instead of writing thousands of lines of code, you can simply drag and link nodes.
                </p>
             </section>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-slate-900 rounded-[32px] border border-slate-700 shadow-xl group hover:border-amber-500/50 transition-all">
                   <h4 className="text-amber-500 font-black text-xs mb-3 uppercase tracking-widest flex items-center gap-2"><i className="fas fa-bolt"></i> Triggers</h4>
                   <p className="text-[11px] text-slate-400 font-medium">The starting point; such as receiving a WhatsApp message, a specific hour, or receiving an HTTP request from your site.</p>
                </div>
                <div className="p-6 bg-slate-900 rounded-[32px] border border-slate-700 shadow-xl group hover:border-purple-500/50 transition-all">
                   <h4 className="text-purple-500 font-black text-xs mb-3 uppercase tracking-widest flex items-center gap-2"><i className="fas fa-brain"></i> Processors</h4>
                   <p className="text-[11px] text-slate-400 font-medium">Where the intelligence happens; such as sentiment analysis, text summarization, or Speech-to-Text (STT).</p>
                </div>
                <div className="p-6 bg-slate-900 rounded-[32px] border border-slate-700 shadow-xl group hover:border-blue-500/50 transition-all">
                   <h4 className="text-blue-500 font-black text-xs mb-3 uppercase tracking-widest flex items-center gap-2"><i className="fas fa-paper-plane"></i> Actions</h4>
                   <p className="text-[11px] text-slate-400 font-medium">The conclusion; such as sending an email, updating the database, or generating a direct voice response.</p>
                </div>
                <div className="p-6 bg-slate-900 rounded-[32px] border border-slate-700 shadow-xl group hover:border-emerald-500/50 transition-all">
                   <h4 className="text-emerald-500 font-black text-xs mb-3 uppercase tracking-widest flex items-center gap-2"><i className="fas fa-link"></i> Edges (Logic)</h4>
                   <p className="text-[11px] text-slate-400 font-medium">Lines that transport data from one node to another with the ability to set conditional logic (If/Else).</p>
                </div>
             </div>
          </div>
        )
      }
    },
    {
      id: 'service-catalog',
      title: { ar: 'مكتبة الخدمات الذكية', en: 'Smart Services Catalog' },
      icon: 'fa-cubes',
      content: {
        ar: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">حلول جاهزة للاستخدام</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                   توفر المنصة مكتبة غنية بالخدمات المتخصصة التي يمكن تفعيلها فوراً أو دمجها في التدفقات المؤتمتة.
                </p>
             </section>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { cat: 'الإعلام والإنتاج', items: 'دوبلاج اللهجات، الموجز الإخباري، كاتب السيناريو' },
                  { cat: 'الإبداع والفنون', items: 'الشاعر النبطي، حكواتي لهجة، مولد الصور التراثية' },
                  { cat: 'الأعمال والشركات', items: 'مراسل الشركات، المستشار القانوني، محلل العقود' },
                  { cat: 'التطوير والبيانات', items: 'تحليل المشاعر، المترجم المحلي، كاشف اللهجة' }
                ].map((c, i) => (
                   <div key={i} className="p-5 bg-slate-50 dark:bg-black/20 rounded-3xl border border-slate-100 dark:border-white/5 transition-all hover:bg-emerald-500/5 group">
                      <h4 className="text-xs font-black text-emerald-500 mb-2 uppercase tracking-widest">{c.cat}</h4>
                      <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 group-hover:text-emerald-600 transition-colors">{c.items}</p>
                   </div>
                ))}
             </div>
          </div>
        ),
        en: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">Ready-to-Use Solutions</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                   The platform provides a rich library of specialized services that can be activated instantly or integrated into automated flows.
                </p>
             </section>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { cat: 'Media & Production', items: 'Dialect Dubbing, News Briefing, Scriptwriter' },
                  { cat: 'Arts & Creativity', items: 'Nabati Poet, Lahja Storyteller, Heritage Image Gen' },
                  { cat: 'Business & Companies', items: 'Corporate Correspondent, Legal Advisor, Contract Analyzer' },
                  { cat: 'Development & Data', items: 'Sentiment Analysis, Local Translator, Dialect Detector' }
                ].map((c, i) => (
                   <div key={i} className="p-5 bg-slate-50 dark:bg-black/20 rounded-3xl border border-slate-100 dark:border-white/5 transition-all hover:bg-emerald-500/5 group">
                      <h4 className="text-xs font-black text-emerald-500 mb-2 uppercase tracking-widest">{c.cat}</h4>
                      <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 group-hover:text-emerald-600 transition-colors">{c.items}</p>
                   </div>
                ))}
             </div>
          </div>
        )
      }
    },
    {
      id: 'agent-center',
      title: { ar: 'مركز الوكلاء المستقلين', en: 'Autonomous Agents Center' },
      icon: 'fa-robot',
      content: {
        ar: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">بناء موظفيك الرقميين</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                   الوكلاء هم أعلى مستوى من الذكاء في منصتنا. الوكيل ليس مجرد روبوت محادثة، بل هو كيان يمكنه التعلم من ملفاتك الخاصة والعمل بشكل مستقل لتنفيذ مهامك.
                </p>
                <div className="bg-emerald-500/10 border-r-4 border-emerald-500 p-8 rounded-[40px] shadow-sm">
                   <h4 className="text-sm font-black text-emerald-600 mb-4">مزايا الوكيل الذكي:</h4>
                   <ul className="text-xs font-bold text-slate-500 dark:text-slate-400 space-y-4">
                      <li className="flex items-center gap-3"><i className="fas fa-check-circle text-emerald-500 text-sm"></i> <span><strong>قاعدة المعرفة:</strong> رفع ملفات (PDF/DOCX) لتدريب الوكيل على بياناتك الخاصة.</span></li>
                      <li className="flex items-center gap-3"><i className="fas fa-check-circle text-emerald-500 text-sm"></i> <span><strong>الهوية الرقمية:</strong> تحديد شخصية، دور، وصوت الوكيل بدقة فائقة.</span></li>
                      <li className="flex items-center gap-3"><i className="fas fa-check-circle text-emerald-500 text-sm"></i> <span><strong>الوصول عبر API:</strong> ربط الوكيل بتطبيقاتك الخارجية أو موقعك الإلكتروني.</span></li>
                   </ul>
                </div>
             </section>
          </div>
        ),
        en: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">Building Your Digital Employees</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                   Agents represent the highest level of intelligence on our platform. An agent is not just a chatbot; it is an entity that can learn from your private files and work independently to execute your tasks.
                </p>
                <div className="bg-emerald-500/10 border-l-4 border-emerald-500 p-8 rounded-[40px] shadow-sm">
                   <h4 className="text-sm font-black text-emerald-600 mb-4">Smart Agent Features:</h4>
                   <ul className="text-xs font-bold text-slate-500 dark:text-slate-400 space-y-4">
                      <li className="flex items-center gap-3"><i className="fas fa-check-circle text-emerald-500 text-sm"></i> <span><strong>Knowledge Base:</strong> Upload files (PDF/DOCX) to train the agent on your private data.</span></li>
                      <li className="flex items-center gap-3"><i className="fas fa-check-circle text-emerald-500 text-sm"></i> <span><strong>Digital Identity:</strong> Define the agent's personality, role, and voice with high precision.</span></li>
                      <li className="flex items-center gap-3"><i className="fas fa-check-circle text-emerald-500 text-sm"></i> <span><strong>API Access:</strong> Connect the agent to your external applications or website.</span></li>
                   </ul>
                </div>
             </section>
          </div>
        )
      }
    },
    {
      id: 'integrations',
      title: { ar: 'بوابة التكامل والربط (Integrations)', en: 'Integration Gateway & Linking' },
      icon: 'fa-network-wired',
      content: {
        ar: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">ربط "لهجة" بحياتك وأعمالك</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                   لا تعيش منصتنا في عزلة؛ بوابة التكامل تتيح لك ربط قدرات الذكاء الاصطناعي مع التطبيقات التي تستخدمها يومياً بمرونة تامة.
                </p>
                <div className="grid grid-cols-1 gap-6">
                   <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/5 rounded-[40px] border border-slate-100 dark:border-white/5 shadow-sm group hover:shadow-xl transition-all">
                      <div className="w-16 h-16 bg-green-500 rounded-3xl flex items-center justify-center text-white text-3xl shadow-lg shrink-0 group-hover:scale-110 transition-transform"><i className="fab fa-whatsapp"></i></div>
                      <div>
                         <h4 className="text-base font-black dark:text-white mb-2">WhatsApp Business</h4>
                         <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold leading-relaxed">الرد التلقائي على العملاء بلهجات محلية، تلخيص الرسائل الصوتية، وجدولة المواعيد بشكل ذكي داخل الواتساب.</p>
                      </div>
                   </div>
                   <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/5 rounded-[40px] border border-slate-100 dark:border-white/5 shadow-sm group hover:shadow-xl transition-all">
                      <div className="w-16 h-16 bg-blue-500 rounded-3xl flex items-center justify-center text-white text-3xl shadow-lg shrink-0 group-hover:scale-110 transition-transform"><i className="fas fa-location-dot"></i></div>
                      <div>
                         <h4 className="text-base font-black dark:text-white mb-2">Google Maps Navigation</h4>
                         <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold leading-relaxed">تغيير صوت الملاحة التقليدي إلى مساعد سعودي ذكي يرشدك بأسلوب ممتع ومحلي يتناسب مع طرقنا وثقافتنا.</p>
                      </div>
                   </div>
                   <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/5 rounded-[40px] border border-slate-100 dark:border-white/5 shadow-sm group hover:shadow-xl transition-all">
                      <div className="w-16 h-16 bg-purple-500 rounded-3xl flex items-center justify-center text-white text-3xl shadow-lg shrink-0 group-hover:scale-110 transition-transform"><i className="fas fa-phone"></i></div>
                      <div>
                         <h4 className="text-base font-black dark:text-white mb-2">Live Call Translator</h4>
                         <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold leading-relaxed">ترجمة فورية للمكالمات الهاتفية من اللغات العالمية إلى اللهجات السعودية مباشرة وبنفس نبرة الصوت.</p>
                      </div>
                   </div>
                </div>
             </section>
          </div>
        ),
        en: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">Connecting "Lahja" to Your Life & Work</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                   Our platform doesn't live in isolation; the integration gateway allows you to link AI capabilities with the applications you use daily with total flexibility.
                </p>
                <div className="grid grid-cols-1 gap-6">
                   <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/5 rounded-[40px] border border-slate-100 dark:border-white/5 shadow-sm group hover:shadow-xl transition-all">
                      <div className="w-16 h-16 bg-green-500 rounded-3xl flex items-center justify-center text-white text-3xl shadow-lg shrink-0 group-hover:scale-110 transition-transform"><i className="fab fa-whatsapp"></i></div>
                      <div>
                         <h4 className="text-base font-black dark:text-white mb-2">WhatsApp Business</h4>
                         <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold leading-relaxed">Auto-reply to customers in local dialects, summarize voice messages, and intelligently schedule appointments directly within WhatsApp.</p>
                      </div>
                   </div>
                   <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/5 rounded-[40px] border border-slate-100 dark:border-white/5 shadow-sm group hover:shadow-xl transition-all">
                      <div className="w-16 h-16 bg-blue-500 rounded-3xl flex items-center justify-center text-white text-3xl shadow-lg shrink-0 group-hover:scale-110 transition-transform"><i className="fas fa-location-dot"></i></div>
                      <div>
                         <h4 className="text-base font-black dark:text-white mb-2">Google Maps Navigation</h4>
                         <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold leading-relaxed">Change the traditional navigation voice to a Saudi smart assistant that guides you in a fun and local style appropriate for our roads and culture.</p>
                      </div>
                   </div>
                   <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/5 rounded-[40px] border border-slate-100 dark:border-white/5 shadow-sm group hover:shadow-xl transition-all">
                      <div className="w-16 h-16 bg-purple-500 rounded-3xl flex items-center justify-center text-white text-3xl shadow-lg shrink-0 group-hover:scale-110 transition-transform"><i className="fas fa-phone"></i></div>
                      <div>
                         <h4 className="text-base font-black dark:text-white mb-2">Live Call Translator</h4>
                         <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold leading-relaxed">Real-time translation for phone calls from global languages into Saudi dialects instantly and with the same voice tone.</p>
                      </div>
                   </div>
                </div>
             </section>
          </div>
        )
      }
    },
    {
      id: 'cli-terminal',
      title: { ar: 'دليل موجه الأوامر والنظام (CLI)', en: 'Command Console & CLI Guide' },
      icon: 'fa-terminal',
      content: {
        ar: (
          <div className="space-y-8">
            <section>
               <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">القوة للمحترفين والمطورين</h3>
               <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                  يوفر موجه الأوامر (Terminal) واجهة تحكم برمجية سريعة لتنفيذ العمليات المعقدة بضغطة زر. يمكنك استخدامه من الصفحة المخصصة أو عبر الاختصار <code>⌘ + K</code>.
               </p>
               
               <div className="p-8 bg-[#050507] rounded-[40px] border border-emerald-500/30 font-mono shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl"></div>
                  <h4 className="text-emerald-500 text-xs mb-8 font-black uppercase tracking-widest border-b border-emerald-500/10 pb-4 flex items-center gap-2">
                    <i className="fas fa-code"></i> قائمة الأوامر التنفيذية (v3.0)
                  </h4>
                  <div className="space-y-8 text-[11px]">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                           <span className="text-white bg-emerald-500/20 px-3 py-1.5 rounded-lg border border-emerald-500/30 block w-fit">flow create [name]</span>
                           <p className="text-slate-500 leading-relaxed">ينشئ تدفق أتمتة جديد فوراً ويفتح المحرر في الواجهة الرئيسية.</p>
                        </div>
                        <div className="space-y-3">
                           <span className="text-white bg-blue-500/20 px-3 py-1.5 rounded-lg border border-blue-500/30 block w-fit">plan upgrade pro</span>
                           <p className="text-slate-500 leading-relaxed">يرفع اشتراكك الحالي للباقة الاحترافية وتفعيل كافة المميزات فوراً.</p>
                        </div>
                        <div className="space-y-3">
                           <span className="text-white bg-purple-500/20 px-3 py-1.5 rounded-lg border border-purple-500/30 block w-fit">service enable [id]</span>
                           <p className="text-slate-500 leading-relaxed">يفعل خدمة معينة من المكتبة عبر المعرّف الفريد للخدمة.</p>
                        </div>
                        <div className="space-y-3">
                           <span className="text-white bg-rose-500/20 px-3 py-1.5 rounded-lg border border-rose-500/30 block w-fit">flow delete [id]</span>
                           <p className="text-slate-500 leading-relaxed">يحذف التدفق المحدد بشكل نهائي من سجلات الأتمتة الخاصة بك.</p>
                        </div>
                     </div>
                  </div>
               </div>
            </section>
          </div>
        ),
        en: (
          <div className="space-y-8">
            <section>
               <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">Power for Professionals & Developers</h3>
               <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                  The Command Console (Terminal) provides a fast programmatic control interface for executing complex operations with a single click. You can use it from the dedicated page or via the shortcut <code>⌘ + K</code>.
               </p>
               
               <div className="p-8 bg-[#050507] rounded-[40px] border border-emerald-500/30 font-mono shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl"></div>
                  <h4 className="text-emerald-500 text-xs mb-8 font-black uppercase tracking-widest border-b border-emerald-500/10 pb-4 flex items-center gap-2">
                    <i className="fas fa-code"></i> Executive Command List (v3.0)
                  </h4>
                  <div className="space-y-8 text-[11px]">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                           <span className="text-white bg-emerald-500/20 px-3 py-1.5 rounded-lg border border-emerald-500/30 block w-fit">flow create [name]</span>
                           <p className="text-slate-500 leading-relaxed">Creates a new automation flow immediately and opens the editor in the main interface.</p>
                        </div>
                        <div className="space-y-3">
                           <span className="text-white bg-blue-500/20 px-3 py-1.5 rounded-lg border border-blue-500/30 block w-fit">plan upgrade pro</span>
                           <p className="text-slate-500 leading-relaxed">Upgrades your current subscription to the Pro plan and activates all features immediately.</p>
                        </div>
                        <div className="space-y-3">
                           <span className="text-white bg-purple-500/20 px-3 py-1.5 rounded-lg border border-purple-500/30 block w-fit">service enable [id]</span>
                           <p className="text-slate-500 leading-relaxed">Activates a specific service from the library using the unique service identifier.</p>
                        </div>
                        <div className="space-y-3">
                           <span className="text-white bg-rose-500/20 px-3 py-1.5 rounded-lg border border-rose-500/30 block w-fit">flow delete [id]</span>
                           <p className="text-slate-500 leading-relaxed">Permanently deletes the specified flow from your automation logs.</p>
                        </div>
                     </div>
                  </div>
               </div>
            </section>
          </div>
        )
      }
    },
    {
      id: 'profile-analytics',
      title: { ar: 'الملف الشخصي والتحليلات', en: 'Profile & Analytics' },
      icon: 'fa-chart-pie',
      content: {
        ar: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">مراقبة الأداء والاستهلاك</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                   توفر لوحة التحكم الشخصية نظرة 360 درجة على نشاطك، استهلاكك، والوقت الذي وفرته باستخدام تقنيات "لهجة" في حياتك اليومية.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                   <div className="p-6 bg-slate-50 dark:bg-white/5 rounded-[32px] border border-slate-100 dark:border-white/5 shadow-sm group hover:bg-emerald-500/5 transition-all">
                      <h4 className="text-[10px] font-black text-slate-400 group-hover:text-emerald-500 uppercase mb-3 tracking-widest">إجمالي الطلبات الكلي</h4>
                      <p className="text-3xl font-black dark:text-white">12,450</p>
                      <div className="h-1 w-12 bg-emerald-500 rounded-full mt-4"></div>
                   </div>
                   <div className="p-6 bg-slate-50 dark:bg-white/5 rounded-[32px] border border-slate-100 dark:border-white/5 shadow-sm group hover:bg-blue-500/5 transition-all">
                      <h4 className="text-[10px] font-black text-slate-400 group-hover:text-blue-500 uppercase mb-3 tracking-widest">الوقت الفعلي الموفر</h4>
                      <p className="text-3xl font-black text-emerald-500">45h</p>
                      <div className="h-1 w-12 bg-blue-500 rounded-full mt-4"></div>
                   </div>
                </div>
             </section>
          </div>
        ),
        en: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">Performance & Consumption Monitoring</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                   The personal dashboard provides a 360-degree view of your activity, consumption, and the time you've saved using "Lahja" technologies in your daily life.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                   <div className="p-6 bg-slate-50 dark:bg-white/5 rounded-[32px] border border-slate-100 dark:border-white/5 shadow-sm group hover:bg-emerald-500/5 transition-all">
                      <h4 className="text-[10px] font-black text-slate-400 group-hover:text-emerald-500 uppercase mb-3 tracking-widest">Total Request Count</h4>
                      <p className="text-3xl font-black dark:text-white">12,450</p>
                      <div className="h-1 w-12 bg-emerald-500 rounded-full mt-4"></div>
                   </div>
                   <div className="p-6 bg-slate-50 dark:bg-white/5 rounded-[32px] border border-slate-100 dark:border-white/5 shadow-sm group hover:bg-blue-500/5 transition-all">
                      <h4 className="text-[10px] font-black text-slate-400 group-hover:text-blue-500 uppercase mb-3 tracking-widest">Actual Time Saved</h4>
                      <p className="text-3xl font-black text-emerald-500">45h</p>
                      <div className="h-1 w-12 bg-blue-500 rounded-full mt-4"></div>
                   </div>
                </div>
             </section>
          </div>
        )
      }
    },
    {
      id: 'security-sovereignty',
      title: { ar: 'الأمن والسيادة الرقمية', en: 'Security & Digital Sovereignty' },
      icon: 'fa-shield-halved',
      content: {
        ar: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">بياناتك داخل حدودك</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-8">
                   نحن نلتزم بأعلى معايير الأمن السيبراني السعودية (SDAIA/NCA). جميع العمليات اللغوية تتم عبر بنية تحتية محلية ومشفرة بالكامل.
                </p>
                <div className="space-y-4">
                   {[
                     { t: 'التشفير التام (AES-256)', d: 'يتم تشفير كافة البيانات المخزنة والمنتقلة بمعايير عسكرية لا يمكن اختراقها.' },
                     { t: 'السيادة اللغوية المحلية', d: 'النماذج مدربة ومستضافة محلياً لضمان عدم تسريب السياق الثقافي الحساس.' },
                     { t: 'إدارة مفاتيح الـ API', d: 'يمكنك إنشاء وحذف مفاتيح الـ API (Secret Keys) الخاصة بك بمرونة تامة وفي أي وقت.' }
                   ].map((item, i) => (
                      <div key={i} className="flex gap-6 p-6 bg-blue-500/5 border border-blue-500/10 rounded-[32px] shadow-sm hover:bg-blue-500/10 transition-all group">
                         <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform"><i className="fas fa-lock"></i></div>
                         <div>
                            <h4 className="text-sm font-black dark:text-white mb-1">{item.t}</h4>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium leading-relaxed">{item.d}</p>
                         </div>
                      </div>
                   ))}
                </div>
             </section>
          </div>
        ),
        en: (
          <div className="space-y-8">
             <section>
                <h3 className="text-xl font-black text-slate-800 dark:text-white mb-4">Your Data within Your Borders</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-8">
                   We adhere to the highest Saudi cybersecurity standards (SDAIA/NCA). All linguistic operations are performed via a fully local and encrypted infrastructure.
                </p>
                <div className="space-y-4">
                   {[
                     { t: 'Full Encryption (AES-256)', d: 'All stored and transmitted data is encrypted with impenetrable military-grade standards.' },
                     { t: 'Local Linguistic Sovereignty', d: 'Models are trained and hosted locally to ensure no sensitive cultural context leaks.' },
                     { t: 'API Key Management', d: 'You can create and delete your API keys (Secret Keys) with total flexibility at any time.' }
                   ].map((item, i) => (
                      <div key={i} className="flex gap-6 p-6 bg-blue-500/5 border border-blue-500/10 rounded-[32px] shadow-sm hover:bg-blue-500/10 transition-all group">
                         <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform"><i className="fas fa-lock"></i></div>
                         <div>
                            <h4 className="text-sm font-black dark:text-white mb-1">{item.t}</h4>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium leading-relaxed">{item.d}</p>
                         </div>
                      </div>
                   ))}
                </div>
             </section>
          </div>
        )
      }
    },
    {
      id: 'faq',
      title: { ar: 'الأسئلة الشائعة (FAQ)', en: 'Frequently Asked Questions (FAQ)' },
      icon: 'fa-circle-question',
      content: {
        ar: (
          <div className="space-y-6">
            {[
               { q: 'كيف أحصل على مفتاح API؟', a: 'يمكنك الحصول عليه من إعدادات الحساب > الأمان بعد الترقية لباقة المحترفين أو من خلال لوحة المفاتيح في ملفك الشخصي.' },
               { q: 'هل بياناتي آمنة داخل المنصة؟', a: 'نعم، نحن نستخدم تشفيراً عسكرياً ونلتزم بسيادة البيانات الوطنية ولا نقوم بتخزين أي محادثات صوتية بعد المعالجة.' },
               { q: 'ما هي أنواع الملفات المدعومة لتدريب الوكيل؟', a: 'ندعم PDF, DOCX, TXT, CSV لتدريب الوكلاء على قاعدة معرفية خاصة بك.' },
               { q: 'هل يدعم المساعد اللغات العالمية؟', a: 'المساعد متخصص في اللهجات السعودية كأولوية، لكنه يستطيع الفهم والتحدث بالإنجليزية بطلاقة تامة.' },
               { q: 'كيف يمكنني إلغاء اشتراكي الحالي؟', a: 'بسهولة من خلال ملفي الشخصي > إدارة الاشتراك، يمكنك التراجع أو تأكيد الإلغاء في أي لحظة.' },
               { q: 'هل يمكنني بناء تدفقات مؤتمتة مخصصة لشركتي؟', a: 'بالتأكيد، مركز التدفق (Flow Canvas) مصمم خصيصاً لبناء مسارات عمل مخصصة تلبي احتياجات أعمالك الفريدة.' }
            ].map((item, i) => (
               <div key={i} className="p-6 bg-white dark:bg-white/5 border border-slate-100 dark:border-white/5 rounded-[32px] shadow-sm hover:shadow-md transition-all group">
                  <h4 className="text-sm font-black text-slate-800 dark:text-white mb-3 flex items-center gap-3">
                     <span className="w-2 h-2 rounded-full bg-emerald-500 group-hover:animate-ping"></span> {item.q}
                  </h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed font-bold pr-5">{item.a}</p>
               </div>
            ))}
          </div>
        ),
        en: (
          <div className="space-y-6">
            {[
               { q: 'How do I get an API key?', a: 'You can obtain it from Account Settings > Security after upgrading to the Pro plan or via the Keys panel in your profile.' },
               { q: 'Is my data secure within the platform?', a: 'Yes, we use military-grade encryption and adhere to national data sovereignty; we do not store any voice conversations after processing.' },
               { q: 'What file types are supported for agent training?', a: 'We support PDF, DOCX, TXT, and CSV for training agents on your private knowledge base.' },
               { q: 'Does the assistant support global languages?', a: 'The assistant specializes in Saudi dialects as a priority, but it can understand and speak English with total fluency.' },
               { q: 'How can I cancel my current subscription?', a: 'Easily through My Profile > Manage Subscription. You can reverse or confirm the cancellation at any moment.' },
               { q: 'Can I build custom automated flows for my company?', a: 'Absolutely. The Flow Canvas is specifically designed to build custom workflows that meet your unique business needs.' }
            ].map((item, i) => (
               <div key={i} className="p-6 bg-white dark:bg-white/5 border border-slate-100 dark:border-white/5 rounded-[32px] shadow-sm hover:shadow-md transition-all group">
                  <h4 className="text-sm font-black text-slate-800 dark:text-white mb-3 flex items-center gap-3">
                     <span className="w-2 h-2 rounded-full bg-emerald-500 group-hover:animate-ping"></span> {item.q}
                  </h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed font-bold pr-5">{item.a}</p>
               </div>
            ))}
          </div>
        )
      }
    }
  ];

  return <DocsComponent language={language} onBack={onBack} docsContent={DOCS_CONTENT} />;
};

export default DocsTemplate;